<template>
  <teleport to="body">
    <v-navigation-drawer v-model="drawerOpen" location="right" temporary width="520">
      <div v-if="evidence" class="ncrm-privacy-evidence-detail pa-5 d-flex flex-column ga-4">
        <div class="d-flex align-start justify-space-between ga-3">
          <div>
            <div class="text-caption text-medium-emphasis">{{ $t(`${copyKey}.eyebrow`, { id: evidence.id }) }}</div>
            <h3 class="text-h6">{{ $t(`${registryKey}.evidenceTypes.${evidence.evidence_type}`) }}</h3>
          </div>
          <v-btn icon="mdi-close" variant="text" :aria-label="$t('app.actions.close')" @click="drawerOpen = false" />
        </div>

        <!-- Ce vede orice rol din registru: datele deja prezente în listă. -->
        <dl class="ncrm-privacy-evidence-detail__fields">
          <template v-for="field in summaryFields" :key="field.key">
            <dt>{{ $t(`${copyKey}.fields.${field.key}`) }}</dt>
            <dd>{{ field.value }}</dd>
          </template>
        </dl>

        <v-alert v-if="!canManage" type="info" icon="mdi-lock-outline">
          {{ $t(`${copyKey}.noPermission`) }}
        </v-alert>
        <v-skeleton-loader v-else-if="loading" type="paragraph, list-item-two-line@3" />
        <v-alert v-else-if="errorKey" :type="errorKey === 'loadError' ? 'error' : 'warning'">
          {{ $t(`${copyKey}.${errorKey}`) }}
          <template v-if="errorKey === 'loadError'" #append>
            <v-btn variant="text" @click="fetchDetail">{{ $t(`${copyKey}.retry`) }}</v-btn>
          </template>
        </v-alert>
        <template v-else-if="detail">
          <div v-if="detail.captured_text">
            <div class="text-caption text-medium-emphasis mb-1">{{ $t(`${copyKey}.fields.capturedText`) }}</div>
            <div class="ncrm-privacy-evidence-detail__text">{{ detail.captured_text }}</div>
          </div>
          <dl class="ncrm-privacy-evidence-detail__fields">
            <template v-for="field in detailFields" :key="field.key">
              <dt>{{ $t(`${copyKey}.fields.${field.key}`) }}</dt>
              <dd :class="{ 'ncrm-privacy-evidence-detail__code': field.code }">{{ field.value }}</dd>
            </template>
          </dl>
          <NcrmPrivacyEvidenceDoiStatus v-if="detail.doi" :doi="detail.doi" />
          <v-alert v-if="downloadFailed" type="error" density="compact">
            {{ $t(`${copyKey}.downloadError`) }}
          </v-alert>
          <v-btn
            v-if="detail.attachment_available"
            color="primary"
            variant="tonal"
            prepend-icon="mdi-download"
            :loading="downloading"
            @click="download"
          >
            {{ $t(`${copyKey}.download`) }}
          </v-btn>
        </template>

        <!-- GDPR-098: retrimiterea din registru, pentru orice rol din registru (nu cere detaliul de
             manager) și pentru capturile fără record CRM. Sub starea DOI a managerului rămâne compactă.
             Montat numai cât drawer-ul e deschis: fiecare deschidere recitește starea (cooldown). -->
        <NcrmPrivacyDoiResendPanel
          v-if="drawerOpen && canResendDoi"
          :pending-event-id="evidence.event.id"
          :embedded="Boolean(detail?.doi)"
          hide-when-settled
          @sent="handleDoiSent"
        />

        <v-btn v-if="recordLink" variant="text" prepend-icon="mdi-open-in-app" :to="recordLink">
          {{ $t(`${copyKey}.openRecord`) }}
        </v-btn>
      </div>
    </v-navigation-drawer>
  </teleport>
</template>

<script>
import axios from "axios";
import { hasRecordAccess } from "@/Utile/ncrmPrivacyAccess.mjs";
import NcrmPrivacyDoiResendPanel from "@/components/ncrm/shared/NcrmPrivacyDoiResendPanel.vue";
import { hasPrivacyRegistryRole } from "@/Utile/ncrmPrivacyAccess.mjs";
import NcrmPrivacyEvidenceDoiStatus from "./NcrmPrivacyEvidenceDoiStatus.vue";
import { formatRegistryDateTime as formatDateTime, recordRoute } from "./privacyWorkspaceContracts.mjs";

const REGISTRY = "ncrm.contacts.dataProtection.workspace.registry";

export default {
  name: "NcrmPrivacyEvidenceDetailDrawer",
  components: { NcrmPrivacyDoiResendPanel, NcrmPrivacyEvidenceDoiStatus },
  props: {
    modelValue: { type: Boolean, default: false },
    // Rândul din registru: `{ id, evidence_type, created_at, attachment_available, event, contact_point, subject }`.
    evidence: { type: Object, default: null },
    // Numai managerul Privacy încarcă detaliul; ruta lui scrie auditul accesului.
    canManage: { type: Boolean, default: false },
  },
  emits: ["update:modelValue", "doi-sent"],
  data: () => ({
    loading: false, errorKey: "", detail: null, requestSequence: 0, downloading: false, downloadFailed: false,
  }),
  computed: {
    copyKey() { return `${REGISTRY}.evidenceDetail`; },
    registryKey() { return REGISTRY; },
    drawerOpen: {
      get() { return this.modelValue; },
      set(value) { this.$emit("update:modelValue", value); },
    },
    summaryFields() {
      const { evidence } = this;
      return [
        { key: "createdAt", value: formatDateTime(evidence.created_at) },
        { key: "event", value: `${this.$t(`ncrm.contacts.dataProtection.record.events.${evidence.event.event_type}`)} · ${formatDateTime(evidence.event.occurred_at)}` },
        { key: "contactPoint", value: evidence.contact_point?.value_display || "—" },
        { key: "subject", value: evidence.subject ? evidence.subject.name || `#${evidence.subject.id}` : "—" },
        { key: "attachment", value: this.$t(`${REGISTRY}.attachmentOptions.${evidence.attachment_available ? "with" : "without"}`) },
      ];
    },
    // Metadata de manager; valorile goale nu ocupă rânduri.
    detailFields() {
      const d = this.detail;
      return [
        { key: "collectedPlace", value: d.collected_place },
        { key: "locale", value: d.locale && d.locale.toUpperCase() },
        { key: "noticeVersion", value: d.notice_version_id && `#${d.notice_version_id}` },
        { key: "formKey", value: d.form_key },
        { key: "delivery", value: d.delivery_id && `#${d.delivery_id}` },
        { key: "fileName", value: d.filename_original },
        { key: "fileType", value: d.mimetype },
        { key: "fileSize", value: d.size_bytes && `${Math.max(1, Math.round(Number(d.size_bytes) / 1024))} KB` },
        { key: "fileHash", value: d.file_sha256, code: true },
        { key: "textHash", value: d.captured_text_hash, code: true },
        { key: "sourceIp", value: d.source_ip },
        { key: "userAgent", value: d.user_agent },
      ].filter((field) => field.value);
    },
    // Dovada unei capturi DOI pe email, din datele sumarului (orice rol din registru, `GDPR-098`); un acord
    // deja confirmat se vede direct la manager, iar la ceilalți panoul se ascunde după starea de pe server.
    canResendDoi() {
      return Boolean(this.evidence?.event?.event_type === "pending_confirmation"
        && this.evidence?.channel_code === "email" && !this.detail?.doi?.confirmed_at && hasPrivacyRegistryRole());
    },
    recordLink() {
      const subject = this.evidence?.subject;
      return subject && hasRecordAccess() ? recordRoute(subject.type, subject.id) : null;
    },
  },
  watch: {
    modelValue(open) {
      if (open) this.fetchDetail();
      else this.requestSequence += 1;
    },
  },
  methods: {
    async fetchDetail() {
      const sequence = ++this.requestSequence;
      this.detail = null; this.errorKey = ""; this.downloadFailed = false;
      // Un rol fără drept nu cere detaliul: serverul nu trimite ce UI-ul ar ascunde.
      if (!this.canManage || !this.evidence) return;
      this.loading = true;
      try {
        const { data } = await axios.get(`/ncrm/privacy/evidence/${this.evidence.id}`);
        if (sequence === this.requestSequence) this.detail = data;
      } catch (error) {
        if (sequence !== this.requestSequence) return;
        const status = Number(error?.response?.status);
        this.errorKey = status === 404 ? "notFound" : status === 403 ? "noPermission" : "loadError";
      } finally {
        if (sequence === this.requestSequence) this.loading = false;
      }
    },
    // Succes: drawer-ul se închide, registrul reîncarcă și arată feedback (`docs/design.md`).
    handleDoiSent(outcome) {
      this.drawerOpen = false;
      this.$emit("doi-sent", outcome);
    },
    // Download-ul trece prin ruta autentificată și auditată; fișierul ajunge ca blob.
    async download() {
      this.downloading = true; this.downloadFailed = false;
      try {
        const response = await axios.get(`/ncrm/privacy/evidence/${this.evidence.id}/download`, { responseType: "blob" });
        const url = URL.createObjectURL(response.data);
        const link = Object.assign(document.createElement("a"), {
          href: url, download: this.detail?.filename_original || `evidence-${this.evidence.id}`,
        });
        link.click();
        // Revocarea imediată poate anula download-ul în unele browsere (Safari).
        setTimeout(() => URL.revokeObjectURL(url), 1000);
      } catch {
        this.downloadFailed = true;
      } finally {
        this.downloading = false;
      }
    },
  },
};
</script>

<style scoped>
.ncrm-privacy-evidence-detail { text-align: left; }
.ncrm-privacy-evidence-detail__fields {
  display: grid; grid-template-columns: minmax(120px, auto) minmax(0, 1fr); gap: 8px 16px; margin: 0; font-size: 14px;
}
.ncrm-privacy-evidence-detail__fields dt { color: rgba(var(--v-theme-on-surface), 0.7); }
.ncrm-privacy-evidence-detail__fields dd { margin: 0; overflow-wrap: anywhere; }
.ncrm-privacy-evidence-detail__code { font-size: 12px; }
.ncrm-privacy-evidence-detail__text {
  white-space: pre-wrap; padding: 12px; border-radius: var(--app-radius-base);
  background: rgba(var(--v-theme-on-surface), 0.05); font-size: 14px;
}
</style>
